import nipplejs, { type JoystickManager, type JoystickOutputData } from 'nipplejs'
import type { InputSource } from './InputSystem'

/**
 * Virtual joystick input source backed by nipplejs ^0.10.x (NOT 1.x — CLAUDE.md
 * contract). The manager is mounted onto a DOM `container` that MUST be a
 * sibling of the Phaser canvas, never the canvas itself — mounting on <canvas>
 * breaks nipplejs's touch event listeners per the README.
 *
 * Visual tokens (color, size, rest opacity) are pulled from UI-SPEC for cozy
 * warmth and touch-screen legibility. Position is anchored bottom-left; the
 * outer container should handle iOS safe-area insets via `env(safe-area-inset-*)`.
 *
 * nipplejs reports y-axis inverted (up = +1); we flip to screen convention
 * (down = +1) so callers can treat the vector identically to keyboard output.
 *
 * No unit test: nipplejs requires real DOM + pointer events that jsdom doesn't
 * faithfully simulate. Integration-verified at scene wiring time in Plan 06.
 */
export class JoystickSource implements InputSource {
  private vec: { x: number; y: number } = { x: 0, y: 0 }
  private readonly manager: JoystickManager

  constructor(container: HTMLElement) {
    this.manager = nipplejs.create({
      zone: container,
      mode: 'static',
      position: { left: '64px', bottom: '64px' },
      color: '#D97757',
      size: 96,
      restOpacity: 0.6,
    })

    this.manager.on('move', (_evt, data: JoystickOutputData) => {
      if (!data?.vector) return
      // nipplejs: up = +1 in y; screen convention: down = +1. Flip sign.
      this.vec = { x: data.vector.x, y: -data.vector.y }
    })
    this.manager.on('end', () => {
      this.vec = { x: 0, y: 0 }
    })
  }

  read(): { x: number; y: number } {
    return this.vec
  }

  destroy(): void {
    this.manager.destroy()
  }
}
